<template>
  <div v-if="group" class="question-group-wrapper">
    <div class="group-card">
      <!-- Group Header (Collapsible) -->
      <div 
        class="group-header" 
        v-b-toggle="'collapse-g-' + group.id"
        role="button"
        :class="{ 'header-collapsed': isCollapsed }"
      >
        <div class="header-content">
          <div class="">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
            </svg>
          </div>
          <h3 class="group-title">{{ group.name }}</h3>
        </div>
        
        <div class="toggle-icon" :class="{ 'collapsed': isCollapsed }">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 9l-7 7-7-7"/>
          </svg>
        </div>
      </div>

      <!-- Questions List (Collapsible Content) -->
      <b-collapse :visible="hasVisibleQuestions && !isMobile" :id="'collapse-g-' + group.id" @show="onShow" @hide="onHide">
        <div class="questions-container" v-if="hasVisibleQuestions">
          <app-question-list-item
            v-for="property of visibleProperties"
            :key="property.id"
            :property="property"
            :isActive="currentQuestionId && currentQuestionId == property.id"
            @currentQuestion="setCurrentQuestion"
            @setAnswer="setAnswer"
            :currentQuestionId="currentQuestionId"
          />
        </div>
      </b-collapse>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import AppQuestionListItem from "@/components/questionnaire/AppQuestionListItem.vue";
import { Group } from "@/entities/questionaire/Group";

@Component({
  components: {
    AppQuestionListItem: AppQuestionListItem,
  },
})
export default class AppQuestionGroup extends Vue {
  @Prop()
  group?: Group;

  @Prop()
  currentQuestionId?: number | null;

  private isCollapsed: boolean = true;
  private isMobile: boolean = window.innerWidth <= 768;

  private onResize() {
    this.isMobile = window.innerWidth <= 768;
  }

  get visibleProperties() {
    if (!this.group || !this.group.properties) return [];
    return this.group.properties.filter((property: any) => {
      return !property.isHidden && !property.hidden;
    });
  }

  get hasVisibleQuestions() {
    return this.visibleProperties && this.visibleProperties.length > 0;
  }

  setCurrentQuestion(data: any) {
    this.$emit("currentQuestion", data);
  }

  setAnswer(data: any) {
    this.$emit("setAnswer", data);
  }

  onShow() {
    this.isCollapsed = false;
  }

  onHide() {
    this.isCollapsed = true;
  }

  mounted() {
    window.addEventListener('resize', this.onResize);
    // Set initial collapsed state based on whether there are visible questions
    if (!this.hasVisibleQuestions) {
      this.isCollapsed = true;
    }
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  }
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

.question-group-wrapper {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  margin-bottom: 0.75rem;
}

.group-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
    border-color: #cbd5e1;
  }
}

/* Group Header - Compact */
.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, rgba(248, 250, 252, 0.6), rgba(241, 245, 255, 0.4));
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid transparent;

  &:hover {
    background: linear-gradient(135deg, rgba(241, 245, 255, 0.7), rgba(232, 240, 254, 0.5));
  }

  &.header-collapsed {
    border-bottom: none;
  }
}

.header-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.group-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #2f45a5, #667bf2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(47, 69, 165, 0.15);

  svg {
    width: 20px;
    height: 20px;
    color: white;
  }
}

.group-title {
  font-size: 1rem;
  font-weight: 600;
 color: #3147A8;
  margin: 0;
  line-height: 1.3;
  word-break: break-word;
}

/* Toggle Icon - Fixed rotation */
.toggle-icon {
  width: 28px;
  height: 28px;
  background: rgba(102, 123, 242, 0.08);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  svg {
    width: 18px;
    height: 18px;
    color: #2f45a5;
    transition: transform 0.2s ease;
  }

  /* When expanded (not collapsed), rotate 180deg to point up */
  &:not(.collapsed) svg {
    transform: rotate(180deg);
  }

  /* When collapsed, no rotation - points down */
  &.collapsed svg {
    transform: rotate(0deg);
  }
}

.group-header:hover .toggle-icon {
  background: rgba(102, 123, 242, 0.12);
  transform: scale(1.05);
}

/* Questions Container - Reduced Padding */
.questions-container {
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.3);
}

/* Collapse Animation */
::v-deep .collapse {
  transition: all 0.25s ease;
}

::v-deep .collapsing {
  transition: height 0.25s ease;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .question-group-wrapper {
    margin-bottom: 0.625rem;
  }

  .group-header {
    padding: 0.875rem 1rem;
    min-height: 56px;
  }

  .header-content {
    gap: 0.625rem;
  }

  .group-icon {
    width: 32px;
    height: 32px;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  .group-title {
    font-size: 0.9375rem;
    line-height: 1.4;
  }

  .toggle-icon {
    width: 28px;
    height: 28px;
    min-width: 44px;
    min-height: 44px;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .questions-container {
    padding: 0.875rem 1rem;
  }

  .group-header {
    -webkit-tap-highlight-color: rgba(102, 123, 242, 0.1);
    user-select: none;
  }
}

@media (max-width: 480px) {
  .question-group-wrapper {
    margin-bottom: 0.5rem;
  }

  .group-card {
    border-radius: 10px;
    max-width: 100%;
    overflow: hidden;
  }

  .group-header {
    padding: 0.75rem 0.875rem;
    min-height: 52px;
  }

  .header-content {
    gap: 0.5rem;
    overflow: hidden;
  }

  .group-icon {
    width: 30px;
    height: 30px;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .group-title {
    font-size: 0.875rem;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .toggle-icon {
    width: 26px;
    height: 26px;
    min-width: 44px;
    min-height: 44px;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .questions-container {
    padding: 0.75rem 0.875rem;
  }
}

/* Extra small devices */
@media (max-width: 360px) {
  .group-header {
    padding: 0.625rem 0.75rem;
  }

  .group-icon {
    width: 28px;
    height: 28px;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  .group-title {
    font-size: 0.8125rem;
  }

  .questions-container {
    padding: 0.625rem 0.75rem;
  }
}

/* Smooth Animation */
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.questions-container {
  animation: slideDown 0.25s ease;
}

/* Focus States */
.group-header:focus {
  outline: 2px solid #2f45a5;
  outline-offset: 2px;
}

.group-header:focus:not(:focus-visible) {
  outline: none;
}

/* Active State */
.group-header:active {
  transform: scale(0.99);
}
</style>